// import { AxiosResponse, AxiosError } from 'axios';

// interface IAPIResponse<T> {
//   status: 'success' | 'fail';
//   message: string;
//   data: T;
// }

// export const handleApiResponse = <T>(response: AxiosResponse<IAPIResponse<T>>) => {
//   return response.data;
// };

// export const handleApiError = (error: AxiosError) => {
//   if (error.response?.data?.error?.message) {
//     throw error.response.data.error.message;
//   } else {
//     throw error.response?.data.message;
//   }
// };

// export const apiGet = async <T>(
//   endpoint: string,
//   config?: Record<string, unknown>,
// ): Promise<T> => {
//   try {
//     const response = await mirageApi.get(endpoint, config);
//     return handleApiResponse<T>(response);
//   } catch (e) {
//     handleApiError(e);
//   }
// };

// export const getLeague = async (
//     tournamentId: number,
// ): Promise<IAPIResponse<ILeague>> => {
//     return mirageApiGet(`/tournaments/${tournamentId}`);
// };

// export const getTeamRatings = async (
//     teamId: number,
// ): Promise<IAPIResponse<ITeam>> => {
//     return mirageApiGet(`/teams/${teamId}/ratings`);
// };

// export const getLeagues = async (): Promise<IAPIResponse<ILeague[]>> => {
//     return mirageApiGet("/leagues");
// };

// In this example, apiGet is a generic function that handles the common logic for making API calls using axios. The handleApiResponse and handleApiError functions are used to process the API response or handle errors consistently across different API calls. The generic type parameter <T> allows you to specify the type of the expected data in the API response.

import axios, {
    AxiosError,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";
import { API_URL, SITE_ID } from "./globals";
import { getTokenFromLocalStorage, getUserFromLocalStorage } from "./utils";
import {
    ICredentials,
    ILocalStorageUserData,
    IPasswordResetParams,
} from "../types/users";
import {
    IAPIResponse,
    IArchivedGame,
    ICoupon,
    IEjectionReport,
    IField,
    IGame,
    IGameLog,
    IMasterTournament,
    IMasterTournamentSchedule,
    INotice,
    IOrder,
    IOrderHistory,
    IOrderProduct,
    IPhoto,
    IPhotoLocation,
    IProduct,
    IProductCategory,
    IPromoCode,
    IPromoCodeType,
    ISite,
    ISiteSetting,
    ITeam,
    ITeamRating,
    ITournament,
    ITournamentSchedule,
    IUmpire,
    IUmpireRating,
    IUmpireRatingAverage,
    IUmpireUser,
    IUser,
    IUserLevel,
    UmpireRatingsData,
} from "../types/api";
import {
    IContactFormData,
    ICreateProductData,
    ICreatePromoCodeData,
    IRegisterUserData,
    ISiteSettingsForm,
    IUserProfileData,
    TUserType,
} from "../types/forms";
import { ScheduleTypes } from "../pages/MySchedule/Schedule/types/myschedule";
import { CoachReviewFormData } from "../pages/MySchedule/CoachReview/types/coachreview";
import { EjectionReportFormData } from "../pages/MySchedule/EjectionReport/types/ejectionreport";
import { LeagueYears } from "../pages/Admin/Umpires/components/Bonus/types/bonus";

// Create an authorized API request instance
const api = axios.create({
    baseURL: API_URL,
    timeout: 20000,
    withCredentials: true,
});

const requestInterceptor = (config: InternalAxiosRequestConfig<any>) => {
    const token = getTokenFromLocalStorage();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-Site-Id"] = SITE_ID;

    const url = new URL(config.url!, config.baseURL);
    config.url = url.toString();

    return config;
};

const responseInterceptors = {
    response: (response: AxiosResponse<any, any>) => {
        if (process.env.BUILD_ENV !== "production") {
            console.log("API Response:", response.data);
        }

        if (response?.data?.token) {
            const userDataRaw = localStorage.getItem("userData");

            if (userDataRaw) {
                let userData: ILocalStorageUserData = JSON.parse(userDataRaw);
                userData.token = response.data.token;
                localStorage.setItem("userData", JSON.stringify(userData));
            }
        }

        return response;
    },
    error: (error: any) => {
        if (process.env.BUILD_ENV !== "production") {
            console.log("API Error:", error);
            error.response.data.error.message =
                error.response.data.error.message +
                "\n\n\n" +
                `File: ${error.response.data.error.file}\n` +
                `Line: ${error.response.data.error.line}\n` +
                error.response.data.error.stack.join("\n");
        }

        return Promise.reject(error);
    },
};

api.interceptors.request.use(config => requestInterceptor(config));
api.interceptors.response.use(
    responseInterceptors.response,
    responseInterceptors.error,
);

export const getSiteSetting = async (
    settingName?: string,
): Promise<IAPIResponse & { setting: ISiteSetting | Array<ISiteSetting> }> => {
    const urlBase = `/site/settings/${SITE_ID}`;
    const url = settingName ? `${urlBase}/${settingName}` : urlBase;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProducts = async (
    activeOnly = true,
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .get(`/products`, { params: { activeOnly } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProductCategories = async (): Promise<
    IAPIResponse & { categories: IProductCategory[] }
> => {
    return api
        .get(`/products/categories`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProduct = async (
    productId: IProduct["id"],
): Promise<IAPIResponse & { product: IProduct }> => {
    return api
        .get(`/products/${productId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createProduct = async (
    product: ICreateProductData,
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
    } = product;
    return api
        .post(
            `/products`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateProduct = async (
    product: ICreateProductData,
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
        tournamentId,
    } = product;
    return api
        .put(
            `/products/${productId}`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
                tournamentId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCodes = async (
    params?: object,
    all = false,
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .get(`/products/promocodes`, { params: { ...params, all } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCode = async (
    promoCodeId: IPromoCode["id"],
): Promise<IAPIResponse & { promoCode: IPromoCode }> => {
    return api
        .get(`/products/promocodes/${promoCodeId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPromoCodeTypes = async (): Promise<
    IAPIResponse & {
        promoCodeTypes: IPromoCodeType[];
    }
> => {
    return api
        .get(`/products/promocodes/types`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPromoCode = async (
    product: ICreatePromoCodeData,
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        excludes,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .post(
            `/products/promocodes`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                excludes,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePromoCode = async (
    product: ICreatePromoCodeData,
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        excludes,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .put(
            `/products/promocodes/${promoCodeId}`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                excludes,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

// export const getField = async (
//     fieldId: number,
// ): Promise<IAPIResponse & { field: IField }> => {
//     return api
//         .get(`/tournaments/fields/${fieldId}`)
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

// export const getAgeLevels = async () => {
//     try {
//         const response = await api.get(`/tournaments/ageLevels`);

//         return response.data;
//     } catch (e) {
//         if (e instanceof AxiosError) {
//             if (e.response?.data?.error?.message) {
//                 throw e.response.data.error.message;
//             } else {
//                 throw e.response?.data.message;
//             }
//         }
//     }
// };

export const getTournaments = async (): Promise<
    IAPIResponse & { tournaments: ITournament[] }
> => {
    return api
        .get(`/tournaments`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournament = async (
    tournamentId: number,
): Promise<IAPIResponse & { tournament: ITournament }> => {
    return api
        .get(`/tournaments/${tournamentId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getMasterTournaments = async (): Promise<
    IAPIResponse & { tournaments: IMasterTournament[] }
> => {
    return api
        .get(`/tournaments/masters`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getMasterTournament = async (
    tournamentId: number,
): Promise<IAPIResponse & { tournament: IMasterTournament }> => {
    return api
        .get(`/tournaments/masters/${tournamentId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpireRatings = async (
    umpireId?: number,
): Promise<IAPIResponse & { ratings: IUmpireRating[] }> => {
    return api
        .get(`/ratings/umpire/${umpireId || ""}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpireRatingAverages = async (
    umpireId?: number,
    averages: boolean = false,
    isAdmin: boolean = false,
): Promise<
    IAPIResponse & { ratings: IUmpireRatingAverage | IUmpireRatingAverage[] }
> => {
    return api
        .get(`/ratings/umpire/${umpireId || ""}`, {
            params: {
                averages,
                isAdmin,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPastUmpireRatingAverages = async (
    umpireId?: number,
): Promise<
    IAPIResponse & { ratings: IUmpireRatingAverage | IUmpireRatingAverage[] }
> => {
    return api
        .get(`/ratings/history/${umpireId || ""}`, {
            params: {
                type: "umpire",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameRatings = async (
    gameId: number,
    type: "umpire" | "team",
): Promise<IAPIResponse & { ratings: Array<ITeamRating | IUmpireRating> }> => {
    return api
        .get(`/ratings/game/${gameId}`, {
            params: {
                ratingType: type,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTournamentSchedule = async (
    tournamentId: number,
): Promise<
    IAPIResponse & { schedule: ITournamentSchedule | IMasterTournamentSchedule }
> => {
    return api
        .get(`/tournaments/${tournamentId}/schedule`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlayoffSchedule = async (tournamentId: number) => {
    try {
        const response = await api.get(
            `/tournaments/${tournamentId}/schedule?playoffs=true`,
        );

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

// export const getBracket = async (
//     tournamentId: number,
// ): Promise<IAPIResponse & { bracket: IPlayoff | IPlayoff[] }> => {
//     return api
//         .get(`/tournaments/${tournamentId}/bracket`)
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

// export const getTeamSchedule = async (
//     teamId: number,
// ): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
//     return api
//         .get(`/tournaments/teams/${teamId}/schedule`)
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

export const getGame = async (
    gameId: IGame["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .get(`/games/${gameId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGamesBySite = async (
    site: number,
    isAdmin: boolean = false,
): Promise<IAPIResponse & { games: IGame[] }> => {
    return api
        .get(`/games/site/${site}`, {
            params: {
                isAdmin,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getArchivedGames = async (
    site: number,
    startDate?: Date | null,
    endDate?: Date | null,
): Promise<IAPIResponse & { games: IArchivedGame[] }> => {
    return api
        .get(`/games/archive`, {
            params: {
                site,
                startDate,
                endDate,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameLogs = async (
    gameId?: IGame["id"],
): Promise<IAPIResponse & { logs: IGameLog[] }> => {
    const urlBase = `/logs/games`;
    const url = gameId ? `${urlBase}/${gameId}` : urlBase;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlaceholderGameLogs = async (
    tournamentId: any,
    date: any,
    field: any,
    diamond: any,
): Promise<IAPIResponse & { logs: IGameLog[] }> => {
    return api
        .get(`/logs/placeholders`, {
            params: {
                tournamentId,
                date,
                field,
                diamond,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeams = async (): Promise<
    IAPIResponse & { teams: ITeam[] }
> => {
    return api
        .get(`/teams`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeam = async (
    teamId: number,
): Promise<IAPIResponse & { team: ITeam }> => {
    return api
        .get(`/teams/${teamId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getUser = async (
    user: IUser["id"],
): Promise<IAPIResponse & { user: IUser }> => {
    return api
        .get(`/users/${user}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUsers = async (
    userType?: IUserLevel["levelName"],
): Promise<IAPIResponse & { users: IUser[] }> => {
    return api
        .get(`/users`, {
            params: userType
                ? {
                      type: userType,
                  }
                : {},
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

// export const getDesignations = async (): Promise<
//     IAPIResponse & { designations: IDesignation[] }
// > => {
//     return api
//         .get(`/tournaments/designations`)
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

// export const getSeasons = async (): Promise<
//     IAPIResponse & { seasons: ISeason[] }
// > => {
//     return api
//         .get(`/tournaments/seasons`)
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

export const getTeamsByAgeLevel = async (
    ageLevel: string,
    year: number,
    season: string,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/teams`, {
            params: {
                ageLevel,
                year,
                season,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpires = async (
    year?: number,
): Promise<
    IAPIResponse & {
        umpires: IUmpireUser[];
    }
> => {
    return api
        .get(`/umpires`, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpire = async (
    umpireId: IUmpireUser["id"],
): Promise<
    IAPIResponse & {
        umpire: IUmpireUser;
    }
> => {
    return api
        .get(`/umpires/${umpireId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getBannedTeamsByUmpire = async (): Promise<
    IAPIResponse & {
        umpires: Array<{
            id: IUmpireUser["id"];
            name: IUmpireUser["name"];
            bannedTeams: UmpireRatingsData["bannedTeams"];
        }>;
    }
> => {
    return api
        .get(`/umpires/banned`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateUmpire = async (
    umpireId: IUmpireUser["id"],
    {
        status,
        comments,
    }: {
        status?: IUmpireUser["status"];
        comments?: IUmpireUser["comments"];
    },
): Promise<IAPIResponse & { umpire: IUmpireUser }> => {
    return api
        .put(
            `/umpires/${umpireId}`,
            {
                status,
                comments,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

// TODO
export const getPaymentIntent = async (
    // cart: IRegisterTeamForm[],
    total: number,
    discount: number,
): Promise<IAPIResponse & { clientSecret: string }> => {
    return api
        .post(
            `/orders/paymentintent/`,
            {
                // cart,
                total,
                discount,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getStripeOrder = async (
    paymentIntentId: string,
): Promise<IAPIResponse & { paymentStatus: string; order: IOrder }> => {
    return api
        .post(
            `/orders/stripestatus/`,
            {
                paymentIntentId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addOrderHistory = async (
    paymentIntentId: string,
    message: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/orders/history`,
            {
                paymentIntentId,
                message,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateOrder = async (
    orderId: IOrder["id"],
    orderStatus: IOrder["status"],
    orderNote?: IOrderHistory["message"],
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .put(
            `/orders/${orderId}`,
            {
                orderStatus,
                orderNote,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const cancelProduct = async (
    orderId: IOrder["id"],
    productId: IOrderProduct["id"],
    teamId: number,
    userId: IUser["id"],
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .put(
            `/products/${productId}/cancel`,
            {
                orderId,
                teamId,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const changeProduct = async (
    orderId: IOrder["id"],
    productId: IOrderProduct["id"],
    oldProductId: IOrderProduct["id"],
    teamId: number,
    userId: IUser["id"],
): Promise<IAPIResponse & { order: IOrder; note?: string }> => {
    return api
        .put(
            `/products/${oldProductId}/change`,
            {
                orderId,
                newProductId: productId,
                teamId,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

/* Used during registration process to retrieve promo code */
export const getCoupon = async (
    couponCode: IPromoCode["code"],
): Promise<IAPIResponse & { coupon: ICoupon | null }> => {
    return api
        .get(`/orders/coupon`, { params: { couponCode } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrder = async (
    orderId: number,
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .get(`/orders/${orderId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrders = async (): Promise<
    IAPIResponse & { orders: IOrder[] }
> => {
    return api
        .get(`/orders`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const updatePhoto = async (
    photoId: IPhoto["id"],
    location: IPhotoLocation["id"] | null,
    userId: IUser["id"],
): Promise<IAPIResponse & { photo: IPhoto }> => {
    return api
        .put(
            `/site/photos/${photoId}`,
            {
                location,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const loginUser = async (
    creds: ICredentials,
): Promise<IAPIResponse & ILocalStorageUserData> => {
    return api
        .post(
            `/users/authenticate`,
            {
                remember: creds.remember,
            },
            {
                auth: {
                    username: creds.email,
                    password: creds.password,
                },
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateUser = async (
    user: IUserProfileData,
): Promise<IAPIResponse> => {
    const {
        id,
        firstName,
        lastName,
        phone,
        email,
        password,
        confirmPassword,
        city,
        state,
        address,
        address2,
        dob,
        gender: { value: gender },
        race: { value: race },
        ssn,
        zipCode,
    } = user;

    return api
        .put(
            `/users/${user.id}`,
            {
                id,
                firstName,
                lastName,
                phone,
                email,
                password,
                confirmPassword,
                city,
                state,
                address,
                address2,
                dob,
                gender,
                race,
                ssn,
                zipCode,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const registerUser = async (
    user: IRegisterUserData,
    userType: TUserType,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users`,
            {
                userType,
                user,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const verifyEmail = async (
    email: IUser["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/verifyemail`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError && e.response) {
                // Log to see if this block is being hit in production
                console.error(
                    "Caught in verifyEmail:",
                    e.response.status,
                    e.message,
                );

                // Throw an error with a specific message
                throw new Error(
                    e.response?.data?.error?.message ||
                        "Email verification failed",
                );
            }
            throw new Error("Unknown error occurred");
            // if (e instanceof AxiosError) {
            //     if (e.response?.data?.error?.message) {
            //         throw e.response.data.error.message;
            //     } else {
            //         throw e.response?.data.message;
            //     }
            // }
        });
};

// TODO Update
export const createOrder = async (
    paymentType: string,
    // cart: IRegisterTeamForm[],
    cartTotal: number,
    discount: number,
    notes?: string,
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .post(
            `/orders`,
            {
                paymentType,
                // cart,
                cartTotal,
                discount,
                notes,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const editorImgUpload = async (file: File): Promise<string> => {
    return api
        .post(
            `/site/photoupload`,
            {
                files: [file],
                editorUpload: true,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data.location;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotos = async (
    location?: string,
): Promise<IAPIResponse & { photos: IPhoto[] }> => {
    return api
        .get(`/site/photos`, { params: { location } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const printRules = async (
    content: string,
): Promise<IAPIResponse & { file: string }> => {
    return api
        .get(`/printer`, { params: { content } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rejectPhoto = async (
    photoId: IPhoto["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/site/photos/${photoId}`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotoLocations = async (): Promise<
    IAPIResponse & { photoLocations: IPhotoLocation[] }
> => {
    return api
        .get(`/site/photolocations`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateSettings = async (
    data: Partial<ISiteSettingsForm>,
): Promise<IAPIResponse> => {
    return api
        .put(
            `/site/settings/${SITE_ID}`,
            {
                data,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

// export const updateGameSummary = async (
//     game: Partial<IGameSummaryData>,
//     gameId: IGame["id"],
//     userId: IUser["id"],
// ): Promise<IAPIResponse> => {
//     const { scores, summary, plateUmpire, fieldUmpire } = game;

//     return api
//         .put(
//             `/games/${gameId}/summary`,
//             {
//                 scores,
//                 summary,
//                 plateUmpire,
//                 fieldUmpire,
//                 userId,
//             },
//             {
//                 headers: {
//                     "Content-Type": "application/x-www-form-urlencoded",
//                 },
//             },
//         )
//         .then(response => {
//             return response.data;
//         })
//         .catch(e => {
//             if (e instanceof AxiosError) {
//                 if (e.response?.data?.error?.message) {
//                     throw e.response.data.error.message;
//                 } else {
//                     throw e.response?.data.message;
//                 }
//             }
//         });
// };

export const updateTeamRatings = async (
    data: Partial<CoachReviewFormData>,
    gameId: IGame["id"],
    userId: IUser["id"],
): Promise<IAPIResponse> => {
    const { home, visitor } = data;

    return api
        .put(
            `/ratings/game/${gameId}`,
            {
                home,
                visitor,
                userId,
                type: "team",
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const lostPassword = async (
    email: ICredentials["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/lostpassword/`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const resetPassword = async ({
    userId,
    password,
    confirmPassword,
}: IPasswordResetParams): Promise<IAPIResponse & { email: IUser["email"] }> => {
    return api
        .post(
            `/users/resetpassword`,
            {
                userId,
                password,
                confirmPassword,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateTokens = async (): Promise<IAPIResponse> => {
    return api
        .get(`/users/validatetokens`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateResetToken = async (
    token: string,
    userId: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/validateresettoken`,
            {
                token,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const submitContact = async (
    data: IContactFormData,
): Promise<IAPIResponse> => {
    const { comment, email, firstName, lastName } = data;

    return api
        .post(
            `/home/contact`,
            {
                comment,
                email,
                firstName,
                lastName,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getNotices = async (): Promise<
    IAPIResponse & { notices: Array<INotice> }
> => {
    return api
        .get(`/site/notice`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateNotice = async (
    siteNotice: string,
): Promise<IAPIResponse & { notice: INotice | false }> => {
    return api
        .put(
            `/site/notice`,
            {
                siteNotice,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const bookGame = async (
    gameId: IGame["id"],
    location: IUmpire["location"],
    user: ILocalStorageUserData["user"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .post(
            `/umpires/book/${gameId}`,
            {
                location,
                userId: user.id,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const bookPlaceholder = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["id"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
    location: IUmpire["location"],
    user: ILocalStorageUserData["user"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .post(
            `/umpires/bookplaceholder`,
            {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
                location,
                userId: user.id,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const adminBookGame = async (
    gameId: IGame["id"],
    location: IUmpire["location"],
    newUmpire?: IUmpire["id"],
    removeUmpire?: number,
    postForReplacement: boolean = false,
    cancelReplacement: boolean = false,
    currentUmpire?: IUmpire,
    overrideDoubleBooking: boolean = false,
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .post(
            `/umpires/adminbook/${gameId}`,
            {
                location,
                newUmpire,
                removeUmpire,
                postForReplacement,
                cancelReplacement,
                currentUmpire,
                overrideDoubleBooking,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const adminBookPlaceholder = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["id"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
    location: IUmpire["location"],
    newUmpire?: IUmpire["id"],
    removeUmpire?: number,
    postForReplacement: boolean = false,
    cancelReplacement: boolean = false,
    currentUmpire?: IUmpire,
    overrideDoubleBooking: boolean = false,
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .post(
            `/umpires/adminbookplaceholder`,
            {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
                location,
                newUmpire,
                removeUmpire,
                postForReplacement,
                cancelReplacement,
                currentUmpire,
                overrideDoubleBooking,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSites = async (): Promise<
    IAPIResponse & { sites: ISite[] }
> => {
    return api
        .get(`/site`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGamesByUmpire = async (
    umpireId: number,
    scheduleType: ScheduleTypes,
): Promise<IAPIResponse & { games: IGame[] }> => {
    return api
        .get(`/umpires/${umpireId}/games`, {
            params: {
                schedule: scheduleType,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const requestReplacement = async (
    gameId: IGame["id"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .put(
            `/umpires/replacement/${gameId}`,
            {
                umpireId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const cancelReplacement = async (
    gameId: IGame["id"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .delete(`/umpires/replacement/${gameId}`, {
            data: {
                umpireId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const cancelBooking = async (
    gameId: IGame["id"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .delete(`/umpires/book/${gameId}`, {
            data: {
                umpireId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const requestPlaceholderReplacement = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["id"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .put(
            `/umpires/placeholderreplacement`,
            {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
                umpireId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const cancelPlaceholderReplacement = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["id"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .delete(`/umpires/placeholderreplacement`, {
            data: {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
                umpireId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const cancelPlaceholderBooking = async (
    tournamentId: number,
    date: IGame["date"],
    endDate: IGame["endDate"],
    ageLevel: IGame["ageLevel"]["id"],
    field: IGame["fieldId"],
    diamond: IGame["diamond"],
    umpireId: IUmpire["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .delete(`/umpires/bookplaceholder`, {
            data: {
                tournamentId,
                date,
                endDate,
                ageLevel,
                field,
                diamond,
                umpireId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createEjection = async (
    data: EjectionReportFormData,
    userId: IUmpire["id"],
): Promise<IAPIResponse> => {
    const { gameId, firstName, lastName, report } = data;
    return api
        .post(
            `/umpires/ejection/${gameId}`,
            {
                userId,
                firstName,
                lastName,
                report,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getEjectionReport = async (
    gameId: IGame["id"],
): Promise<IAPIResponse & { report: IEjectionReport }> => {
    return api
        .get(`/umpires/ejection/${gameId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAllEjectionReports = async (): Promise<
    IAPIResponse & { reports: IEjectionReport[] }
> => {
    return api
        .get(`/umpires/ejection`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getYears = async (): Promise<IAPIResponse & LeagueYears> => {
    return api
        .get(`/leagues/years`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getFieldsList = async (
    gamesOnly?: boolean,
): Promise<IAPIResponse & { fields: IField[] }> => {
    const endpoint = gamesOnly
        ? `/games/fields?umpires=true`
        : `/leagues/fields?umpires=true`;
    return api
        .get(endpoint)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export default api;
